import React from 'react';
import { useAnalytics } from '@Context/AnalyticsContext';
import AnalyticsFilters from './AnalyticsFilters';
import AnalyticsSummary from './AnalyticsSummary';
import AnalyticsChartContainer from './charts/AnalyticsChartContainer';
import { ArrowsClockwise, Warning } from '@phosphor-icons/react';

const AnalyticsDashboard: React.FC = () => {
  const { loading, error, refreshData, setFilters } = useAnalytics();

  if (error) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-gray-400 bg-red-50 p-4">
          <div className="flex items-center space-x-2">
            <Warning size={20} className="text-red-600" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Error Loading Analytics Data
              </h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={refreshData}
                disabled={loading}
                className="mt-2 inline-flex items-center rounded border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 shadow-sm hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <ArrowsClockwise size={14} weight="fill" className="mr-1" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-blue-900">
            AI Usage Analytics
          </h2>
          <p className="text-sm text-blue-900">
            Manage and monitor your AI model’s token consumption with detailed
            usage insights.
          </p>
        </div>

        <div className="mt-4 flex items-center space-x-3 sm:mt-0">
          <button
            onClick={refreshData}
            disabled={loading}
            className="rounded-full p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            title="Refresh data"
          >
            <ArrowsClockwise
              size={24}
              weight="fill"
              className={
                loading ? 'animate-spin text-gray-600' : 'text-gray-600'
              }
            />
          </button>

          <button
            onClick={() =>
              setFilters({
                user: undefined,
                model: undefined,
                feature: undefined,
              })
            }
            disabled={loading}
            className="rounded border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            title="Clear Filters"
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-400 bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Filters & Chart Type
        </h2>
        <AnalyticsFilters />
      </div>

      <div className="rounded-lg border border-gray-400 bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Summary</h2>
        <AnalyticsSummary />
      </div>

      <div className="rounded-lg border border-gray-400 bg-white p-6 shadow">
        <AnalyticsChartContainer />
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
